import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fmtShortDate, type GoLiveQueue, type GoLiveRow } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import type { QueryState } from '../../useQuery';
import { ConfirmDialog, withConfirm } from '../../ui/ConfirmDialog';
import { useReadOnlyReason } from '../../ui/ReadOnly';

/**
 * One go-live row as the queue and the To do's Pilots and go-lives print it (design glRows):
 * its state line and tone, the progress bar, and the projection.
 */
export function pilotView(r: GoLiveRow, who: string | null) {
  const live = r.state === 'pilot';
  const day = live ? r.pilot?.day ?? null : null;
  const late60 = live && day !== null && day >= 60;
  const tone = r.stuck ? 'act' : late60 ? 'warn' : live ? 'good' : 'muted';
  const waiting = who ?? (r.waitsOn === 'facility' ? r.contact?.name ?? 'the facility' : 'Aithora');
  const pct = r.progress.total ? Math.round((r.progress.done / r.progress.total) * 100) : 0;
  const state = live
    ? `Live · pilot day ${day ?? '?'} of ${r.pilot?.pilotDays ?? 90}`
    : r.stuck
      ? `Stuck ${r.lastMovedDays ?? '?'} days · waiting on ${waiting}`
      : `Moving · last step ${r.lastMovedDays ? `${r.lastMovedDays}d ago` : 'today'}`;
  const proj = live
    ? late60
      ? 'Day 60+: start conversion'
      : `Converts ${fmtShortDate(r.pilot && 'endDate' in r.pilot ? r.pilot.endDate : null)}`
    : r.projectedGoLive
      ? `On pace for ${fmtShortDate(r.projectedGoLive)}`
      : '–';
  return { live, late60, tone, pct, state, proj, fill: r.stuck ? 'act' : live ? 'good' : 'accent' };
}

/**
 * The Operator's go-live queue line (handoff: "Operator / Super Admin: Go-live queue";
 * design: glShow, glRows, glAccess). One line; Open queue drops a panel over the page
 * and never pushes it down.
 * - "Send test page" pages the signed-in person (POST /notify/test-push), confirm-first,
 *   then reads the test (GET /notify/test/:id) until it is acknowledged ("reached your
 *   phone in 5s"). Once a person has proved their alerts, the line no longer asks
 *   (myAlerts on the queue, PROPOSED).
 * - Nudge (POST /onboarding/:id/nudge) texts and emails the Facility Admin contact; Take it
 *   (POST /onboarding/:id/take) makes the caller the Aithora owner.
 */
export function GoLiveLine({ q, open: openProp, onOpenChange }: { q: QueryState<GoLiveQueue>; open?: boolean; onOpenChange?: (open: boolean) => void }) {
  const { client, session } = useSession();
  const roReason = useReadOnlyReason();
  const readOnly = roReason !== null;
  // Open queue may also be opened from the To do tab's Pilots and go-lives (design glToggle).
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (f: (o: boolean) => boolean) => {
    const next = f(open);
    setOpenState(next);
    onOpenChange?.(next);
  };
  const [confirming, setConfirming] = useState(false);
  // The test page sent this session: its id while waiting, then its acknowledgement.
  const [test, setTest] = useState<null | { id: string; ack: number | null } | 'failed'>(null);
  const [nudged, setNudged] = useState<Record<string, string>>({});
  const [took, setTook] = useState<Record<string, boolean>>({});
  const polls = useRef(0);
  const s = q.data?.summary;
  const rows = q.data?.rows ?? [];
  const stuck = rows.filter((r) => r.stuck);
  const mine = q.data?.myAlerts;
  // Shown until acknowledged once (design: glAlertOpen = never tested, or tested this session).
  // Without myAlerts (not served yet) the test is still offered, with no claim about the past.
  const alertOpen = (mine?.ackSeconds ?? null) === null || test !== null;
  const sending = test !== null && test !== 'failed' && test.ack === null;

  // After the page goes out, read the test until the person taps it.
  const testId = test !== null && test !== 'failed' ? test.id : null;
  useEffect(() => {
    if (!sending || !testId || polls.current >= 60) return;
    const t = setTimeout(async () => {
      polls.current += 1;
      try {
        const st = await api.testStatus(client, { params: { id: testId } });
        setTest({ id: testId, ack: st.ackSeconds });
      } catch {
        setTest({ id: testId, ack: null });
      }
    }, 1000);
    return () => clearTimeout(t);
  }, [sending, testId, test, client]);

  const nudge = async (r: GoLiveRow) => {
    const res = await api.goLiveNudge(client, { params: { facilityId: r.facilityId }, body: {} });
    setNudged((n) => ({ ...n, [r.facilityId]: new Date(res.sentAt).toTimeString().slice(0, 5) }));
  };
  const take = async (r: GoLiveRow) => {
    await api.goLiveTake(client, { params: { facilityId: r.facilityId }, body: {} });
    setTook((t) => ({ ...t, [r.facilityId]: true }));
  };

  return (
    <div className="golive" data-testid="golive" data-screen-label="Go-live queue">
      <div className="golive__line">
        <span className="golive__title">Go-live queue</span>
        {q.loading && !q.data && <span className="golive__next">Loading{'…'}</span>}
        {q.error && !q.data && <span className="golive__next">Could not load (request {q.error.requestId ?? 'no id'})</span>}
        {s && (
          <>
            <span className="golive__count">{`${s.goingLive} going live · ${s.inPilot} in pilot`}</span>
            {s.stuck > 0 && (
              <span className="golive__stuck">
                <i />
                {`${s.stuck} stuck · ${stuck[0]?.name.split(' ')[0] ?? ''}`}
              </span>
            )}
            <span className="golive__next" title={s.next ? `Projected from their pace so far, ${s.next.basis.toUpperCase()}` : undefined}>
              {s.next ? `Next: ${s.next.name}, on pace for ${fmtShortDate(s.next.onPaceFor)}` : ''}
            </span>
          </>
        )}
        {alertOpen && (
          <span className="golive__alerts">
            <b>Your alerts:</b>
            {`${test === null ? (mine ? ' not tested' : '') : test === 'failed' ? ' could not send' : test.ack === null ? ' sending…' : ` reached your phone in ${test.ack}s`}`}
            {test === null && (
              <button type="button" className="golive__test" onClick={() => setConfirming(true)} disabled={readOnly} title={readOnly ? roReason : undefined}>
                Send test page
              </button>
            )}
          </span>
        )}
        <button type="button" className="golive__open" aria-expanded={open} onClick={() => setOpen((o) => !o)} disabled={!q.data}>
          {open ? 'Close' : 'Open queue'}
        </button>
      </div>
      {open && q.data && (
        <div className="golive__panel" role="dialog" aria-label="Go-live queue">
          <div className="golive__grid golive__grid--head">
            <span>Facility</span>
            <span>Progress</span>
            <span>Theirs</span>
            <span>Ours</span>
            <span>Projected</span>
            <span />
          </div>
          {rows.map((r) => {
            const live = r.state === 'pilot';
            const day = live ? r.pilot?.day ?? null : null;
            const late60 = live && day !== null && day >= 60;
            const tone = r.stuck ? 'act' : late60 ? 'warn' : live ? 'good' : 'muted';
            const who = r.waitsOn === 'facility' ? r.contact?.name ?? 'the facility' : 'Aithora';
            const pct = r.progress.total ? Math.round((r.progress.done / r.progress.total) * 100) : 0;
            const nudgedAt = nudged[r.facilityId] ?? (r.nudgedAt ? new Date(r.nudgedAt).toTimeString().slice(0, 5) : null);
            const yours = took[r.facilityId] || (r.owner !== null && r.owner.userId === session?.user.id);
            return (
              <div key={r.facilityId} className="golive__grid">
                <span className="golive__name">
                  <span className="golive__fac">{r.name}</span>
                  <span className={`golive__state golive--${tone}`}>
                    {live
                      ? `Live · pilot day ${day ?? '?'} of ${r.pilot?.pilotDays ?? 90}`
                      : r.stuck
                        ? `Stuck ${r.lastMovedDays ?? '?'} days · waiting on ${who}`
                        : `Moving · last step ${r.lastMovedDays ? `${r.lastMovedDays}d ago` : 'today'}`}
                  </span>
                </span>
                <span className="golive__prog">
                  <span className="golive__progn">{`${r.progress.done} of ${r.progress.total}`}</span>
                  <span className="golive__bar">
                    <span className={`golive__fill golive__fill--${r.stuck ? 'act' : live ? 'good' : 'accent'}`} style={{ width: `${pct}%` }} />
                  </span>
                </span>
                <span className="golive__cell">{live ? '–' : r.theirs ?? '–'}</span>
                <span className="golive__cell">{live ? '–' : r.ours ?? '–'}</span>
                <span className={`golive__proj${r.stuck || late60 ? ' golive__proj--warn' : ''}`} title={live ? undefined : 'Projected from their pace so far, MODELED'}>
                  {live
                    ? late60
                      ? 'Day 60+: start conversion'
                      : `Converts ${fmtShortDate(r.pilot && 'endDate' in r.pilot ? r.pilot.endDate : null)}`
                    : r.projectedGoLive
                      ? `On pace for ${fmtShortDate(r.projectedGoLive)}`
                      : '–'}
                </span>
                <span className="golive__acts">
                  {!live && r.waitsOn === 'facility' && (
                    <button
                      type="button"
                      className={`golive__btn${nudgedAt ? ' golive__btn--done' : ' golive__btn--primary'}`}
                      disabled={readOnly || !!nudgedAt}
                      title={readOnly ? roReason : undefined}
                      onClick={() => nudge(r)}
                    >
                      {nudgedAt ? `Nudged ${nudgedAt}` : `Nudge ${(r.contact?.name ?? 'contact').split(' ')[0]}`}
                    </button>
                  )}
                  {live ? (
                    <Link to="/performance" className="golive__btn">
                      Open
                    </Link>
                  ) : (
                    <button type="button" className={`golive__btn${yours ? ' golive__btn--done' : ''}`} disabled={readOnly || yours} title={readOnly ? roReason : undefined} onClick={() => take(r)}>
                      {yours ? 'Yours' : 'Take it'}
                    </button>
                  )}
                </span>
              </div>
            );
          })}
          {q.data.access && q.data.access.length > 0 && (
            <div className="golive__access">
              <span className="golive__acch">Access · Super Admin</span>
              {q.data.access.map((a) => (
                <div key={a.userId} className="golive__accrow">
                  <span className="golive__accwho">{a.who}</span>
                  <span className="golive__accwhat">{a.what}</span>
                  {a.pending && (
                    <Link to="/settings" className="golive__approve">
                      Approve
                    </Link>
                  )}
                  <span className={`golive__accstate ${a.pending ? 'golive--warn' : 'golive--good'}`}>{a.pending ? 'Pending' : 'Active'}</span>
                </div>
              ))}
            </div>
          )}
          <div className="golive__foot">
            Theirs comes from each Facility Admin{'’'}s setup checklist. Projected date is their pace so far, MODELED. Pilot clock starts at go-live (FOUNDING50: 90 days).
          </div>
        </div>
      )}
      {confirming && (
        <ConfirmDialog
          title="Send yourself a test page?"
          body="Aithora sends a real page to your phone (push, then SMS after 5 minutes if it is not opened). It proves your alerts reach you. Nobody else is paged."
          confirmLabel="Send test page"
          onConfirm={async () => {
            try {
              const r = await api.testPush(client, { body: withConfirm({ user_id: session?.user.id }) });
              polls.current = 0;
              setTest({ id: r.testId, ack: null });
            } catch (e) {
              setTest('failed');
              throw e;
            }
          }}
          onClose={() => setConfirming(false)}
        />
      )}
    </div>
  );
}
